import type { SessionUser } from '../lib/api'
import { AuthNotice } from './AuthNotice'

type GateAction = { label: string; onClick: () => void; primary?: boolean }

/**
 * The one place the console says *waiting* versus *blocked* (docs/CONTRACT.md §11). Both call
 * sites — the sign-in form and a persisted session being re-checked — render this, so the wording
 * cannot drift between them.
 */
export function AccountGateNotice({ user, actions }: { user: SessionUser; actions: GateAction[] }) {
  // The status decides the headline; "not an administrator" is only the reason once the account
  // is approved. A rejected non-admin is rejected first.
  const title =
    user.status === 'pending'
      ? 'Waiting for approval'
      : user.status === 'rejected'
        ? 'This account was turned down'
        : user.status === 'suspended'
          ? 'This account is suspended'
          : 'This account is not an administrator'

  return (
    <AuthNotice
      tone={user.status === 'pending' ? 'warn' : 'danger'}
      title={title}
      actions={actions.map((action) => (
        <button
          key={action.label}
          type="button"
          onClick={action.onClick}
          className={
            action.primary
              ? 'press min-h-11 rounded-full bg-accent px-5 text-ui font-semibold text-on-accent hover:bg-accent-strong'
              : 'press min-h-11 rounded-full px-5 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2'
          }
        >
          {action.label}
        </button>
      ))}
    >
      <p>
        <bdi className="font-medium text-ink">{user.displayName}</bdi>{' '}
        <span className="text-muted">@{user.handle}</span>
      </p>
      {user.status === 'pending' && (
        <p>
          This account has signed up and an administrator has not approved it yet. Nothing else is
          loading while it waits.
        </p>
      )}
      {user.status === 'rejected' && (
        <p>An administrator turned this sign-up down. Ask whoever runs this server if that seems wrong.</p>
      )}
      {user.status === 'suspended' && (
        <p>Access for this account was suspended. Ask an administrator if that seems wrong.</p>
      )}
      {user.status === 'approved' && !user.isAdmin && (
        <p>The console only signs in administrator accounts. This account can still use the chat app.</p>
      )}
    </AuthNotice>
  )
}
